/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext, IActionInput, IActionWrapperPrototype, IObservableAction } from '@msdyn365-commerce/core-internal';
import { AsyncResult } from '@msdyn365-commerce/retail-proxy';
import { dataActionOptions, wrapDataAction } from './wrap-data-action';

/**
 * Higher order function which exposes the current method as an action
 * @param action The action method
 * @param isBatched If the service call can handle batched requests efficiently
 * @param input Optional parameter of a single instance or an array of IActionInputs that will be passed to the action method.
 */
export const createObservableDataAction = <TOut>(options: dataActionOptions<TOut>): IObservableAction<TOut> => {
    const wrappedAction = wrapDataAction(
        options,
        (arg1: IActionInput | IActionInput[], arg2: IActionContext): AsyncResult<TOut> => {
            return new AsyncResult((resolve, reject) => {
                arg2.chainAction<TOut>(options.action, arg1)
                    .then(result => {
                        resolve(<TOut>result);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        }
    );
    (<IActionWrapperPrototype>wrappedAction.prototype).setProperty('isObservable', true);
    return <IObservableAction<TOut>>wrappedAction;
};
